import { forwardRef, useId } from 'react'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'
import ru from 'react-phone-number-input/locale/ru'
import { useTranslation } from 'react-i18next'
import { RequiredMark, RequiredCaption } from '@/shared/ui/atoms/required-mark'
import 'react-phone-number-input/style.css'
import './phone-number-input.css'

type E164Number = string

export interface PhoneNumberInputProps {
  value?: string | null
  onChange?: (value: E164Number | undefined) => void
  label?: string
  name?: string
  id?: string
  /** Defaults to `'tel'` (browser autofill for phone numbers). */
  autoComplete?: string
  'aria-label'?: string
  width?: string
  status?: 'error' | 'warning'
  errorData?: Record<string, string | null | undefined>
  required?: boolean
  field?: string
  dispatch?: (action: { type: string; field: string; value: string | null }) => void
  country?: string
  international?: boolean
  withCountryCallingCode?: boolean
  maxLength?: number
  disabled?: boolean
  readOnly?: boolean
  height?: string
  backgroundColor?: string
}

export const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  function PhoneNumberInput(
    {
      value,
      onChange,
      label,
      name,
      id: idProp,
      autoComplete = 'tel',
      'aria-label': ariaLabel,
      width,
      status,
      errorData,
      required,
      field,
      dispatch,
      country,
      international,
      withCountryCallingCode,
      maxLength = 13,
      disabled,
      readOnly,
      height = '35px',
      backgroundColor = 'transparent',
    },
    ref,
  ) {
    const { t } = useTranslation()
    const fallbackId = useId()
    const id = idProp ?? fallbackId
    const accessibleName = ariaLabel ?? label ?? t('phone_number')

    const handleBlur = () => {
      if (!dispatch || !field) return
      if (value && !isValidPhoneNumber(value as string)) {
        dispatch({
          type: 'SET_ERROR_DATA',
          field,
          value: t('invalid_phoneNumber'),
        })
      } else {
        dispatch({ type: 'SET_ERROR_DATA', field, value: null })
      }
    }

    return (
      <div
        className='glasspro-phone-input flex flex-col gap-2 relative'
        style={{ width: width ?? '100%' }}
      >
        {label && (
          <label
            htmlFor={id}
            className='text-xs font-medium leading-normal text-(--form-header-title-color)'
          >
            {label} {required && <RequiredMark />}
          </label>
        )}
        <PhoneInput
          flags={flags}
          international={international}
          withCountryCallingCode={withCountryCallingCode}
          labels={ru}
          country={country}
          defaultCountry='UZ'
          name={name ?? id}
          numberInputProps={{
            id,
            ref,
            autoComplete,
            'aria-label': accessibleName,
          }}
          placeholder={t('phone_number')}
          maxLength={maxLength}
          value={value ?? undefined}
          onChange={onChange ?? (() => {})}
          disabled={disabled}
          readOnly={readOnly}
          onBlur={handleBlur}
          className='rounded-[1px] !border border-[#d9d9d9] transition-all duration-300 ease-in-out hover:border-[#1890ff] focus-within:!border-[#1890ff] font-normal tracking-normal text-left px-2.5 py-0.5 text-black placeholder:text-[#d9d9d9]'
          style={{
            width: width ?? '100%',
            height,
            background: backgroundColor,
          }}
          error={
            value
              ? isValidPhoneNumber(value)
                ? undefined
                : t('invalid_phoneNumber')
              : t('phone_number_required')
          }
        />
        {status === 'error' && errorData && (
          <RequiredCaption errorData={errorData} field={field ?? label ?? ''} />
        )}
      </div>
    )
  },
)
